/**
 * Fills the cart upsell rail from Shopify's product recommendations endpoint
 * (sections/stln-cart-upsell-recommendations), so the rail can include Search &
 * Discovery's auto-generated recommendations, which Liquid can't read directly.
 *
 * Kept unnoticeable:
 * - The cart drawer is rendered (closed) on every page, so the first fetch runs
 *   in the background when the browser is idle, before the drawer is opened.
 * - Results are cached in sessionStorage per cart contents, so navigating between
 *   pages shows the rail instantly. The saved copy is only a placeholder: a fresh one
 *   is fetched every time and swapped in (unless a card is open or mid-add).
 * - The element carries data-skip-subtree-update, so the cart's morph re-render
 *   keeps the current rail and only updates data-url / data-cart-product-ids.
 *   A change there hides any card now in the cart straight away, then swaps in
 *   the fresh rail when the new fetch lands.
 */

const CACHE_PREFIX = 'stln-cart-upsell:';
const CACHE_TTL = 5 * 60 * 1000;

class StlnCartUpsellLoader extends HTMLElement {
  static observedAttributes = ['data-url', 'data-cart-product-ids'];

  /** @type {AbortController | null} */
  #controller = null;

  connectedCallback() {
    this.#load({ idle: true });
  }

  disconnectedCallback() {
    this.#controller?.abort();
  }

  /**
   * @param {string} _name
   * @param {string | null} oldValue
   * @param {string | null} newValue
   */
  attributeChangedCallback(_name, oldValue, newValue) {
    if (!this.isConnected || oldValue === null || oldValue === newValue) return;
    this.#hideCardsInCart();
    this.#load({ idle: false });
  }

  get #cacheKey() {
    return `${CACHE_PREFIX}${this.dataset.url}|${this.dataset.cartProductIds}`;
  }

  /**
   * Stale-while-revalidate: a saved rail shows instantly, then a fresh one is always fetched and
   * swapped in, so a theme update or a Search & Discovery change never stays hidden behind the
   * saved copy.
   * @param {{ idle: boolean }} options
   */
  #load({ idle }) {
    const key = this.#cacheKey;
    const cached = this.#readCache(key);
    if (cached !== null) this.#render(cached);

    const url = this.dataset.url;
    if (!url) return;

    this.#controller?.abort();
    const controller = new AbortController();
    this.#controller = controller;

    const run = () => {
      fetch(url, { signal: controller.signal })
        .then((response) => (response.ok ? response.text() : Promise.reject(new Error(`HTTP ${response.status}`))))
        .then((text) => {
          const doc = new DOMParser().parseFromString(text, 'text/html');
          const html = doc.querySelector('.stln-upsell')?.outerHTML ?? '';
          this.#writeCache(key, html);
          // The cart may have changed again while this was in flight.
          if (this.#cacheKey === key) this.#render(html);
        })
        .catch((error) => {
          if (error?.name !== 'AbortError') console.warn('[stln-cart-upsell] Could not load recommendations:', error);
        });
    };

    if (idle && 'requestIdleCallback' in window) {
      window.requestIdleCallback(run, { timeout: 1500 });
    } else {
      run();
    }
  }

  /** @param {string} html */
  #render(html) {
    if (this.innerHTML === html) return;
    // Don't pull the rail out from under someone who is picking a size or mid-add.
    if (this.querySelector('stln-upsell-quick-add-component[open], stln-upsell-quick-add-component[adding]')) return;
    this.innerHTML = html;
  }

  /** Stale-while-revalidate: drop cards for products that just entered the cart. */
  #hideCardsInCart() {
    const inCart = new Set((this.dataset.cartProductIds ?? '').split(','));
    for (const card of this.querySelectorAll('stln-upsell-quick-add-component[data-product-id]')) {
      if (!(card instanceof HTMLElement) || !inCart.has(card.dataset.productId ?? '')) continue;
      const slide = card.closest('slideshow-slide');
      if (slide instanceof HTMLElement) slide.hidden = true;
    }
  }

  /**
   * @param {string} key
   * @returns {string | null}
   */
  #readCache(key) {
    try {
      const raw = sessionStorage.getItem(key);
      if (!raw) return null;
      const { time, html } = JSON.parse(raw);
      if (Date.now() - time > CACHE_TTL) return null;
      return typeof html === 'string' ? html : null;
    } catch {
      return null;
    }
  }

  /**
   * @param {string} key
   * @param {string} html
   */
  #writeCache(key, html) {
    try {
      sessionStorage.setItem(key, JSON.stringify({ time: Date.now(), html }));
    } catch {
      // Storage full or blocked: the rail still renders, just without the cache.
    }
  }
}

if (!customElements.get('stln-cart-upsell-loader')) {
  customElements.define('stln-cart-upsell-loader', StlnCartUpsellLoader);
}
